/**
 * Blog Service
 * 
 * Handles all blog-related API calls.
 */

import api from '../utils/api';

/**
 * Create a new blog post
 * @param {Object} blogData - Blog data (title, content, images, category, tags, status, created_by, etc.)
 * @returns {Promise} - Create blog response
 */
export const createBlog = async (blogData) => {
  try {
    // Prepare request body as JSON (not FormData)
    const requestBody = {
      title: blogData.title || '',
      content: blogData.content || '',
      status: blogData.status ? blogData.status.toUpperCase() : 'DRAFT',
      created_by: blogData.created_by || '',
    };
    
    // Add images array if provided
    if (blogData.images && Array.isArray(blogData.images) && blogData.images.length > 0) {
      requestBody.images = blogData.images;
    }
    
    // Add video URL if provided
    if (blogData.video) {
      requestBody.video = blogData.video;
    }
    
    // Add category name if provided
    if (blogData.category) {
      requestBody.category = blogData.category;
    }
    
    // Add tags as array
    if (blogData.tags && blogData.tags.length > 0) {
      requestBody.tags = Array.isArray(blogData.tags) ? blogData.tags : [blogData.tags];
    }

    // Send JSON request
    const response = await api.post('/v1/blogs/', requestBody);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog created successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to create blog',
      data: null,
    };
  } catch (error) {
    console.error('Create blog error:', error);
    
    // Extract error message from response
    let errorMessage = 'Failed to create blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details) {
        errorMessage = errorData.details;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      retryAfter: error.retryAfter,
    };
  }
};

/**
 * Update a blog post (full update using PUT)
 * @param {number|string} blogId - Blog ID
 * @param {Object} blogData - Fields to update (all fields optional)
 * @returns {Promise} - Update blog response
 */
export const updateBlog = async (blogId, blogData) => {
  try {
    const response = await api.put(`/v1/blogs/${blogId}`, blogData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update blog',
      data: null,
    };
  } catch (error) {
    console.error('Update blog error:', error);
    
    let errorMessage = 'Failed to update blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a blog post (PATCH) - for quick status updates
 * @param {number|string} blogId - Blog ID
 * @param {Object} blogData - Fields to update (only include fields to update)
 * @returns {Promise} - Update blog response
 */
export const updateBlogPartial = async (blogId, blogData) => {
  try {
    const response = await api.patch(`/v1/blogs/${blogId}`, blogData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update blog',
      data: null,
    };
  } catch (error) {
    console.error('Update blog partial error:', error);
    
    let errorMessage = 'Failed to update blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};


/**
 * Get all blogs
 * @param {Object} params - Query parameters (skip, limit, status_filter, category, search)
 * @returns {Promise} - Get blogs response
 */
export const getBlogs = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.status_filter) queryParams.status_filter = params.status_filter.toUpperCase();
    if (params.category) queryParams.category = params.category;
    if (params.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/blogs/${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Blogs retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve blogs',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get blogs error:', error);
    
    let errorMessage = 'Failed to retrieve blogs. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get a single blog by ID
 * @param {number|string} blogId - Blog ID
 * @returns {Promise} - Get blog response
 */
export const getBlog = async (blogId) => {
  try {
    const response = await api.get(`/v1/blogs/${blogId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve blog',
      data: null,
    };
  } catch (error) {
    console.error('Get blog error:', error);
    
    let errorMessage = 'Failed to retrieve blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a blog post
 * @param {number|string} blogId - Blog ID
 * @returns {Promise} - Delete blog response
 */
export const deleteBlog = async (blogId) => {
  try {
    const response = await api.delete(`/v1/blogs/${blogId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Blog deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete blog',
    };
  } catch (error) {
    console.error('Delete blog error:', error);
    
    let errorMessage = 'Failed to delete blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Like a blog post
 * @param {number|string} blogId - Blog ID
 * @returns {Promise} - Like blog response
 */
export const likeBlog = async (blogId) => {
  try {
    const response = await api.post(`/v1/blogs/${blogId}/like`);

    if (response.status === 'success') {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog liked successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to like blog',
      data: null,
    };
  } catch (error) {
    console.error('Like blog error:', error);
    
    let errorMessage = 'Failed to like blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Unlike a blog post
 * @param {number|string} blogId - Blog ID
 * @returns {Promise} - Unlike blog response
 */
export const unlikeBlog = async (blogId) => {
  try {
    const response = await api.delete(`/v1/blogs/${blogId}/like`);

    if (response.status === 'success') {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Blog unliked successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to unlike blog',
      data: null,
    };
  } catch (error) {
    console.error('Unlike blog error:', error);
    
    let errorMessage = 'Failed to unlike blog. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const blogService = {
  createBlog,
  updateBlog,
  updateBlogPartial,
  getBlogs,
  getBlog,
  deleteBlog,
  likeBlog,
  unlikeBlog,
};
export default blogService;
